import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useMedplum } from '@medplum/react'
import { IconCheck, IconUserCheck } from '@tabler/icons-react'
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Divider,
  Group,
  Modal,
  NumberInput,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core'
import { USDateInput } from '@/components/ui/USDateInput'
import { FIELD_CLASSES, MODAL_CONTENT } from '@/components/ui/formModal.tokens'
import { cn } from '@/utils/cn'
import { AdminPanel } from './AdminPanel'
import { UnderlineTabs } from './UnderlineTabs'
import {
  HINT,
  ID_CHIP,
  PRIMARY_BTN,
  SECONDARY_BTN,
  SUBTLE_BTN,
  TABLE_HEAD,
  TABLE_ROW,
} from './tokens'
import { formatCalendarDay, formatWallClockTime } from './zocdocFormat'
import { BookSlotModal, type SlotToBook } from './BookSlotModal'
import { LinkPractitionerModal, type LinkTarget } from './LinkPractitionerModal'
import { useClient, type OrpcClient } from '@/hooks/useClient'

type ProviderLinksResult = Awaited<ReturnType<OrpcClient['zocdocProviderLinks']>>
type ProviderLink = ProviderLinksResult['links'][number]
type CandidatesResult = Awaited<ReturnType<OrpcClient['zocdocPractitionerCandidates']>>
type PractitionerCandidate = NonNullable<CandidatesResult['candidates']>[number]
type LinkResult = Awaited<ReturnType<OrpcClient['zocdocLinkPractitioner']>>

/**
 * A refused link, in the words an operator can act on. Both directions are refusals rather than
 * updates for the same reason: overwriting either side would re-point one doctor's calendar at
 * another person.
 */
const conflictMessage = (conflict: NonNullable<LinkResult['conflict']>) =>
  conflict.kind === 'practitioner-has-other-provider'
    ? `That practitioner is already linked to Zocdoc provider ${conflict.existing}. Linking them to this one as well would leave two Zocdoc providers pointing at one calendar, so nothing was changed.`
    : `This Zocdoc provider is already linked to practitioner ${conflict.existing}. Pointing it at a second practitioner would send its bookings to the wrong calendar, so nothing was changed.`
type ProviderDetailsResult = Awaited<ReturnType<OrpcClient['zocdocProviderDetails']>>

/**
 * What Zocdoc's directory calls one `pr_…|lo_…`, keyed by that composite id.
 *
 * Our own rows come from `/v1/schedulable_entities`, which carries no name at all — so the name
 * has to come from `/v1/providers?npis=…`, and it is keyed on `providerLocationId` rather than
 * NPI because one provider at two locations is two rows that must not read identically.
 */
type EntityLabel = {
  name?: string
  locationName?: string
  virtual?: boolean
  city?: string
  state?: string
}

/** Zocdoc's own cap on one provider-details call, so a large table is read in batches. */
const NPI_BATCH = 100

const buildEntityLabels = (results: ProviderDetailsResult[]) => {
  const byEntity = new Map<string, EntityLabel>()
  for (const provider of results.flatMap((result) => result.providers ?? [])) {
    const name = [provider.firstName, provider.lastName].filter(Boolean).join(' ')
    for (const location of provider.locations ?? []) {
      byEntity.set(location.providerLocationId, {
        ...(name && { name }),
        ...(location.locationName && { locationName: location.locationName }),
        virtual: location.virtual,
        ...(location.city && { city: location.city }),
        ...(location.state && { state: location.state }),
      })
    }
  }
  return byEntity
}

const groupCandidatesByNpi = (results: CandidatesResult[]) => {
  const byNpi = new Map<string, PractitionerCandidate[]>()
  for (const candidate of results.flatMap((result) => result.candidates ?? [])) {
    byNpi.set(candidate.npi, [...(byNpi.get(candidate.npi) ?? []), candidate])
  }
  return byNpi
}

/** What the profile popup is showing: which link was clicked, and what Zocdoc said about it. */
type Profile = {
  link: ProviderLink
  loading: boolean
  result?: ProviderDetailsResult
  error?: string
}

type AvailabilityResult = Awaited<ReturnType<OrpcClient['zocdocSetAvailability']>>
type SlotsResult = Awaited<ReturnType<OrpcClient['zocdocProviderSlots']>>

/** What the slots calendar popup is showing. */
type SlotsView = {
  link: ProviderLink
  fromDay: string
  toDay: string
  loading: boolean
  /** Which half of the location model is on screen. Survives a refresh of the same popup. */
  tab: SlotTab
  result?: SlotsResult
  error?: string
}

/** Weekday label for a `YYYY-MM-DD`, read in UTC so it matches the calendar date itself. */
const weekdayLabel = (date: string) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'short',
    timeZone: 'UTC',
  })

/**
 * `2026-08-31T09:30:00` → `9:30 am`. Left as-is if Zocdoc sent something unexpected.
 *
 * Zocdoc's published timeslots carry a bare local wall clock, so this reshapes the characters and
 * applies no zone — the provider's own clock is the one to show here.
 */
/**
 * The locations V1 publishes availability to: telehealth only.
 *
 * On-site availability stays Zocdoc's to manage — we do not hold the room facts that make an
 * in-person slot real, so publishing one would advertise a visit nobody reserved space for. The
 * on-site rows are still *shown*, because Zocdoc's per-date overwrite reaches them regardless.
 */
const telehealthOf = (locations?: { providerLocationId: string; virtual?: boolean }[]) =>
  (locations ?? []).filter((location) => location.virtual)

const slotTime = (startTime?: string) =>
  startTime && startTime.length >= 16 ? formatWallClockTime(startTime.slice(11, 16)) : '—'

type SlotsDay = SlotsResult['days'][number]
type Slot = SlotsDay['slots'][number]
type SlotLocation = SlotsResult['locations'][number]

/** The two halves of Zocdoc's location model, which the popup shows one at a time. */
type SlotTab = 'virtual' | 'onSite'

/**
 * Which tab a slot belongs to.
 *
 * A slot the middleware could not decode (`location` absent) falls to **on-site**: an undecodable
 * id is not evidence of telehealth, and the on-site tab carries the banner naming those ids. It is
 * never hidden — an invisible slot would read as an empty calendar, which is the one thing this
 * screen must not lie about.
 */
const tabOf = (slot: Slot): SlotTab => (slot.location?.virtual ? 'virtual' : 'onSite')

const slotsForTab = (days: SlotsDay[], tab: SlotTab): SlotsDay[] =>
  days.map((day) => ({ ...day, slots: day.slots.filter((slot) => tabOf(slot) === tab) }))

const countSlots = (days: SlotsDay[]) => days.reduce((sum, day) => sum + day.slots.length, 0)

/**
 * The decoded location, split into a name and a raw id.
 *
 * When Zocdoc supplies no `location_name` the id is all there is — and an id is the one thing that
 * must NOT be shoved into an uppercase badge, which is how `lo_5s71aooko…` became the unreadable
 * `LO_5S71AOOKO…`. So the id travels separately and gets monospace treatment.
 */
const describeLocation = (location: SlotLocation) => {
  const where = [location.address, location.city, location.state].filter(Boolean).join(', ')
  return {
    name: location.name ?? null,
    where: where || null,
    id: location.locationId,
    timeZone: location.timeZone ?? null,
  }
}

/**
 * One colour per location, so a time published to several locations reads as several offers
 * rather than as duplicates. Cycles past the end; a slot with no known location gets none.
 */
const LOCATION_COLORS = ['#EAB308', '#2563EB', '#DC2626', '#F97316', '#92400E']

/**
 * Slots sharing a start time and patient type, in day order — one time published to several
 * locations is one button. Patient type stays part of the key: new and existing are different offers.
 */
const groupByTime = (slots: Slot[]): Slot[][] => {
  const groups = new Map<string, Slot[]>()
  for (const slot of slots) {
    const key = `${slot.startTime ?? slot.timeslotId}|${slot.patientType ?? ''}`
    groups.set(key, [...(groups.get(key) ?? []), slot])
  }
  return [...groups.values()]
}

const locationLabel = (slot: Slot) => slot.location?.name ?? slot.locationId ?? 'Unknown location'

const SLOT_TABS = [
  { value: 'virtual', label: 'Virtual visits' },
  { value: 'onSite', label: 'On site' },
] as const satisfies readonly { value: SlotTab; label: string }[]

/**
 * One tab's calendar: the slots belonging to this half of Zocdoc's location model, headed by the
 * locations it is showing.
 *
 * A real seven-column week rather than wrapping fixed-width columns — the old version broke a week
 * into 5 + 2 and orphaned Sunday and Monday on a second row, which is exactly the reading a
 * calendar must not force.
 */
function SlotsTabPanel({
  days,
  locations,
  unresolvedLocationIds,
  label,
  onBook,
}: {
  days: SlotsDay[]
  locations: SlotLocation[]
  unresolvedLocationIds: string[]
  label: string
  onBook: (day: string, slot: Slot) => void
}) {
  const total = countSlots(days)
  const colorOf = new Map(
    locations.map((location, index) => [
      location.locationId,
      LOCATION_COLORS[index % LOCATION_COLORS.length],
    ]),
  )
  const colorOfSlot = (slot: Slot) => (slot.locationId ? colorOf.get(slot.locationId) : undefined)
  /** A time free at several locations, waiting for the operator to pick one. */
  const [picking, setPicking] = useState<{ day: string; slots: Slot[] } | null>(null)

  return (
    <div className="flex flex-col gap-3">
      {/* Decoding the ids is the point of the header: `lo_KZv…` tells an operator nothing. */}
      {locations.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {locations.map((location) => {
            const decoded = describeLocation(location)
            return (
              <div
                key={decoded.id}
                className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-lg border border-l-4 border-[#ECEEF1] bg-[#FBFCFD] px-2.5 py-1.5"
                style={{ borderLeftColor: colorOf.get(decoded.id) }}
              >
                {decoded.name ? (
                  <span className="text-[13px] font-bold text-[#1F2733]">{decoded.name}</span>
                ) : (
                  <span className={ID_CHIP}>{decoded.id}</span>
                )}
                {decoded.where && <span className={HINT}>{decoded.where}</span>}
                {decoded.timeZone && <span className={HINT}>· {decoded.timeZone}</span>}
              </div>
            )
          })}
        </div>
      ) : (
        <p className={cn([HINT, 'm-0'])}>
          Zocdoc describes no {label.toLowerCase()} location for this provider.
        </p>
      )}

      {unresolvedLocationIds.length > 0 && (
        <Alert
          color="yellow"
          radius="md"
          title="Some slots have a location Zocdoc did not describe"
        >
          <span className="text-[13px]">
            Shown here rather than hidden, so the count stays honest. Undecodable{' '}
            {unresolvedLocationIds.length === 1 ? 'id' : 'ids'}: {unresolvedLocationIds.join(', ')}
          </span>
        </Alert>
      )}

      {total === 0 ? (
        <p className={cn([HINT, 'm-0'])}>No {label.toLowerCase()} slots in this range.</p>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
          {days.map((day) => (
            <div
              key={day.date}
              className="flex min-w-0 flex-col gap-1.5 rounded-xl border border-[#ECEEF1] bg-[#FBFCFD] p-2"
            >
              <div>
                <div className="text-[13px] font-extrabold tracking-[-0.2px] text-[#1F2733]">
                  {weekdayLabel(day.date)}
                </div>
                <div className={HINT}>{formatCalendarDay(day.date)}</div>
              </div>

              {day.error ? (
                <span className="text-[12px] font-bold text-[#DC2626]">
                  read failed — {day.error}
                </span>
              ) : day.slots.length === 0 ? (
                <span className={HINT}>no slots</span>
              ) : (
                <div className="flex flex-col gap-1">
                  {groupByTime(day.slots).map((group) => {
                    const [first] = group
                    return (
                      <button
                        key={first.timeslotId}
                        type="button"
                        onClick={() =>
                          group.length === 1
                            ? onBook(day.date, first)
                            : setPicking({ day: day.date, slots: group })
                        }
                        title={[
                          // The decoded names when we have them; the raw id is the honest
                          // fallback rather than a blank.
                          group.map(locationLabel).join(', '),
                          first.timeZone,
                          first.patientType,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                        className={cn([
                          // A slot is a button, so it looks like one: raised surface, hairline,
                          // coral on hover. The old light badge gave no affordance at all.
                          'behuman-v2-mulish flex w-full cursor-pointer items-stretch gap-1.5 rounded-md border bg-white px-1 py-1 text-[12.5px] font-bold transition-colors outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#E5533D]',
                          'hover:border-[#E5533D] hover:bg-[#FCF1EE] hover:text-[#E5533D]',
                          // Existing-patient slots are a different offer, not a different state, so
                          // they carry a tint rather than a louder colour.
                          first.patientType === 'existing'
                            ? 'border-[#DDD6FE] text-[#5B21B6]'
                            : 'border-[#E4E7EB] text-[#1F2733]',
                        ])}
                      >
                        {/* One strip per location this time is free at. */}
                        <span className="flex shrink-0 gap-0.5" aria-hidden>
                          {group.map((slot) => (
                            <span
                              key={slot.timeslotId}
                              className="w-[3px] rounded-full bg-[#CBD5E1]"
                              style={{ backgroundColor: colorOfSlot(slot) }}
                            />
                          ))}
                        </span>
                        <span className="min-w-0 flex-1 text-center">
                          {slotTime(first.startTime)}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal
        classNames={{
          content: MODAL_CONTENT,
          title: '!text-[17px] !font-extrabold !tracking-[-0.3px]',
        }}
        radius={18}
        opened={picking !== null}
        onClose={() => setPicking(null)}
        size="sm"
        title={
          picking
            ? `${weekdayLabel(picking.day)} ${formatCalendarDay(picking.day)} · ${slotTime(picking.slots[0].startTime)}`
            : ''
        }
      >
        {picking && (
          <Stack gap="xs">
            <Text className={HINT}>
              Free at {picking.slots.length} locations — pick one to book.
            </Text>
            {picking.slots.map((slot) => (
              <button
                key={slot.timeslotId}
                type="button"
                onClick={() => {
                  setPicking(null)
                  onBook(picking.day, slot)
                }}
                className="behuman-v2-mulish flex w-full cursor-pointer items-center gap-2.5 rounded-md border border-[#E4E7EB] bg-white px-3 py-2 text-left text-[13px] font-bold text-[#1F2733] transition-colors outline-none hover:border-[#E5533D] hover:bg-[#FCF1EE] hover:text-[#E5533D] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#E5533D]"
              >
                <span
                  className="h-4 w-1 shrink-0 rounded-full bg-[#CBD5E1]"
                  style={{ backgroundColor: colorOfSlot(slot) }}
                  aria-hidden
                />
                {locationLabel(slot)}
              </button>
            ))}
          </Stack>
        )}
      </Modal>
    </div>
  )
}

/** 0 = Sunday … 6 = Saturday, matching the middleware contract. */
const WEEKDAYS = [
  { value: '1', label: 'Mon' },
  { value: '2', label: 'Tue' },
  { value: '3', label: 'Wed' },
  { value: '4', label: 'Thu' },
  { value: '5', label: 'Fri' },
  { value: '6', label: 'Sat' },
  { value: '0', label: 'Sun' },
]

type HoursDraft = {
  fromDay: string
  toDay: string
  weekdays: string[]
  startTime: string
  endTime: string
  slotMinutes: number
}

/** `YYYY-MM-DD`, offset from today. */
const isoDay = (offsetDays: number) => {
  const date = new Date()
  date.setDate(date.getDate() + offsetDays)
  return date.toISOString().slice(0, 10)
}

const hoursDefault = (): HoursDraft => ({
  fromDay: isoDay(1),
  toDay: isoDay(7),
  weekdays: ['1', '2', '3', '4', '5'],
  startTime: '09:00',
  endTime: '17:00',
  slotMinutes: 30,
})

/**
 * Provider links: Zocdoc's own provider/location roster, and which beHuman practitioner each
 * pair maps to.
 *
 * **Nothing is stored and there is nothing to discover.** The list is Zocdoc's roster read live
 * and joined to the practitioners carrying each `pr_…` as a FHIR identifier, so an entity Zocdoc
 * adds appears on the next load and one it removes stops appearing. Mapping is the identifier
 * write — Link to beHuman profile — and it is the only thing on this screen that writes anything.
 */
export function ProviderLinksCard() {
  const client = useClient()
  // Open profile and Slots read Zocdoc's live roster by NPI — a diagnostic for whoever is
  // debugging the integration, not something the people administering links need.
  const isSuperAdmin = useMedplum().isSuperAdmin()
  const [data, setData] = useState<ProviderLinksResult | null>(null)
  const [labels, setLabels] = useState<{ byEntity: Map<string, EntityLabel>; error?: string }>({
    byEntity: new Map(),
  })
  /**
   * beHuman practitioners that carry each NPI on screen. Keyed by NPI because that is the only
   * key the match has — see the candidates contract for why a name is never offered.
   */
  const [candidates, setCandidates] = useState<{
    byNpi: Map<string, PractitionerCandidate[]>
    error?: string
  }>({ byNpi: new Map() })
  /** Which provider the manual link popup is open for, if any. */
  const [linkTarget, setLinkTarget] = useState<LinkTarget | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [hours, setHours] = useState<HoursDraft>(hoursDefault)
  /**
   * The `pr_…|lo_…` pairs the publish form will send, ticked in the locations table. Seeded with
   * every location the directory returned, so the default stays "publish everywhere" — narrowing
   * to, say, telehealth only is an explicit act.
   */
  const [publishLocationIds, setPublishLocationIds] = useState<Set<string>>(new Set())
  const [availability, setAvailability] = useState<AvailabilityResult | null>(null)
  const [publishing, setPublishing] = useState(false)
  const [confirmPublish, setConfirmPublish] = useState(false)
  const [slots, setSlots] = useState<SlotsView | null>(null)
  const [booking, setBooking] = useState<SlotToBook | null>(null)

  /**
   * One group per NPI — per *doctor* — because a provider practising at several locations is
   * several Zocdoc entities, and reading the same name down four rows hides that.
   *
   * Order follows the links themselves rather than being sorted, so a refresh does not
   * reshuffle the table. An entity Zocdoc gave no NPI for still gets a group: it cannot be
   * attributed to a doctor, and dropping it from the table would hide a real link.
   *
   * The name is taken from whatever the directory returned for the group's rows, and *all* of
   * them are shown when they disagree — one NPI can carry several providers, so a single name
   * would be a guess.
   */
  const togglePublishLocation = (providerLocationId: string) =>
    setPublishLocationIds((current) => {
      const next = new Set(current)
      if (!next.delete(providerLocationId)) next.add(providerLocationId)
      return next
    })

  const toggleAllPublishLocations = (
    locations: { providerLocationId: string }[],
    include: boolean,
  ) =>
    setPublishLocationIds((current) => {
      const next = new Set(current)
      for (const location of locations) {
        if (include) next.add(location.providerLocationId)
        else next.delete(location.providerLocationId)
      }
      return next
    })

  const groups = useMemo(() => {
    const byNpi = new Map<string, { npi?: string; links: ProviderLink[] }>()
    for (const link of data?.links ?? []) {
      const key = link.npi ?? `no-npi:${link.zocdocEntityId}`
      const group = byNpi.get(key) ?? { ...(link.npi && { npi: link.npi }), links: [] }
      group.links.push(link)
      byNpi.set(key, group)
    }
    return [...byNpi.entries()].map(([key, group]) => ({
      key,
      ...group,
      /**
       * Telehealth only — that is what beHuman delivers today (`Encounter.class = VR`), and the
       * in-person rows are not something this screen administers. A location the directory has
       * not described is left out rather than claimed as telehealth: the filter must not guess.
       */
      telehealth: group.links.filter(
        (link) => labels.byEntity.get(link.zocdocEntityId)?.virtual === true,
      ),
      names: [
        ...new Set(
          group.links
            .map((link) => labels.byEntity.get(link.zocdocEntityId)?.name)
            .filter((name): name is string => !!name),
        ),
      ],
      specialties: [
        ...new Set(group.links.map((link) => link.mainSpecialtyName).filter((it) => !!it)),
      ],
    }))
  }, [data, labels])

  /** Every location on screen in the profile popup — the denominator for the publish selection. */
  const profileLocationCount = (profile?.result?.providers ?? []).reduce(
    (count, provider) => count + (provider.locations?.length ?? 0),
    0,
  )

  /**
   * Links, then the names for them — one load, so the table arrives already readable rather than
   * filling in a beat later under a second spinner.
   *
   * The directory read is deliberately *not* per row: every distinct NPI goes out together, so a
   * page of links costs one call (batched only if it exceeds Zocdoc's own 100 cap). Its failure
   * is caught separately from the links read — a directory outage must leave a table of real
   * links standing, identified by id as before, not blank it.
   */
  const load = useCallback(async () => {
    setBusy(true)
    try {
      const links = await client.zocdocProviderLinks()
      const npis = [
        ...new Set(links.links.map((link) => link.npi).filter((npi): npi is string => !!npi)),
      ]
      let byEntity = new Map<string, EntityLabel>()
      let labelsError: string | undefined
      if (npis.length > 0) {
        const batches: string[][] = []
        for (let at = 0; at < npis.length; at += NPI_BATCH) {
          batches.push(npis.slice(at, at + NPI_BATCH))
        }
        try {
          const results = await Promise.all(
            batches.map((batch) => client.zocdocProviderDetails({ npis: batch })),
          )
          byEntity = buildEntityLabels(results)
          // `ok: false` is a reached-but-refused Zocdoc, which does not throw.
          labelsError = results.find((result) => !result.ok)?.error
        } catch (cause) {
          labelsError = cause instanceof Error ? cause.message : 'Could not read provider names'
        }
      }
      // Its own read and its own error: Zocdoc's directory and our own practitioner records fail
      // independently, and a table of real links must survive either one going dark.
      let byNpi = new Map<string, PractitionerCandidate[]>()
      let candidatesError: string | undefined
      if (npis.length > 0) {
        const batches: string[][] = []
        for (let at = 0; at < npis.length; at += NPI_BATCH) {
          batches.push(npis.slice(at, at + NPI_BATCH))
        }
        try {
          const results = await Promise.all(
            batches.map((batch) => client.zocdocPractitionerCandidates({ npis: batch })),
          )
          byNpi = groupCandidatesByNpi(results)
          candidatesError = results.find((result) => !result.ok)?.error
        } catch (cause) {
          candidatesError =
            cause instanceof Error ? cause.message : 'Could not read beHuman practitioners'
        }
      }

      setData(links)
      setLabels({ byEntity, ...(labelsError && { error: labelsError }) })
      setCandidates({ byNpi, ...(candidatesError && { error: candidatesError }) })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load provider links')
    } finally {
      setBusy(false)
    }
  }, [client])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * Reads the provider straight from Zocdoc's directory by NPI rather than from our own row —
   * the point of the popup is to see what Zocdoc holds, which is where the name lives at all
   * (the schedulable-entities feed our rows come from carries no name).
   */
  const openProfile = async (link: ProviderLink) => {
    if (!link.npi) return
    setProfile({ link, loading: true })
    // Each provider gets a fresh form and no stale result from the previously opened popup.
    setHours(hoursDefault())
    setAvailability(null)
    setConfirmPublish(false)
    setPublishLocationIds(new Set())
    try {
      const result = await client.zocdocProviderDetails({ npis: [link.npi] })
      setProfile({ link, loading: false, result })
      // Telehealth only, and only telehealth can be ticked (see the locations table): V1 publishes
      // virtual availability. On-site rows are shown because Zocdoc's overwrite still reaches them
      // — see the confirm — but they are not ours to fill.
      setPublishLocationIds(
        new Set(
          (result.providers ?? []).flatMap((provider) =>
            (provider.locations ?? [])
              .filter((location) => location.virtual)
              .map((location) => location.providerLocationId),
          ),
        ),
      )
    } catch (cause) {
      setProfile({
        link,
        loading: false,
        error: cause instanceof Error ? cause.message : 'Could not reach middleware',
      })
    }
  }

  /**
   * Builds slots from the popup's working-hours pattern via `POST /v2/zocdoc/availability`.
   *
   * `dryRun` is the whole safety story: middleware only sends to Zocdoc when it is false, and each
   * PUT there **replaces** that provider's entire slot list for the date. So Preview is always
   * available, and Publish goes through a confirm that names the dates being overwritten.
   *
   * `timeZone` is deliberately not sent — middleware adopts the zone Zocdoc reports for the
   * schedulable entity, which is the only value Zocdoc itself vouches for, and echoes it back.
   *
   * `locationIds` carries the ticked rows of the locations table, so slots can be published for
   * telehealth alone. It does not make the untick*ed* locations safe: Zocdoc replaces the whole
   * date for the provider, so those locations lose that date's slots either way — the confirm
   * says so.
   */
  const submitAvailability = async (dryRun: boolean) => {
    if (!profile?.link.npi) return
    setPublishing(true)
    setConfirmPublish(false)
    setAvailability(null)
    try {
      setAvailability(
        await client.zocdocSetAvailability({
          npi: profile.link.npi,
          fromDay: hours.fromDay,
          toDay: hours.toDay,
          weekdays: hours.weekdays.map(Number) as (0 | 1 | 2 | 3 | 4 | 5 | 6)[],
          startTime: hours.startTime,
          endTime: hours.endTime,
          slotMinutes: hours.slotMinutes,
          locationIds: [...publishLocationIds],
          dryRun,
        }),
      )
    } catch (cause) {
      setProfile((current) =>
        current
          ? {
              ...current,
              error: cause instanceof Error ? cause.message : 'Could not reach middleware',
            }
          : current,
      )
    } finally {
      setPublishing(false)
    }
  }

  /**
   * Reads the slots already on this provider's Zocdoc calendar via
   * `GET /v2/zocdoc/availability`. Read-only — it publishes nothing.
   *
   * Defaults to the coming week. Zocdoc requires a date per request, so middleware fans out one
   * call per day and caps the window at 31 days; widening the range here costs a request a day.
   */
  // `tab` is threaded through rather than reset, so changing the date range or refreshing does not
  // bounce the reader back to the other half of the calendar.
  const fetchSlots = async (
    link: ProviderLink,
    fromDay: string,
    toDay: string,
    tab: SlotTab = 'virtual',
  ) => {
    if (!link.npi) return
    setSlots({ link, fromDay, toDay, tab, loading: true })
    try {
      const result = await client.zocdocProviderSlots({ npi: link.npi, fromDay, toDay })
      setSlots({ link, fromDay, toDay, tab, loading: false, result })
    } catch (cause) {
      setSlots({
        link,
        fromDay,
        toDay,
        tab,
        loading: false,
        error: cause instanceof Error ? cause.message : 'Could not reach middleware',
      })
    }
  }

  const openSlots = (link: ProviderLink) => fetchSlots(link, isoDay(0), isoDay(6))

  /**
   * A clicked slot → everything the booking popup needs, flattened here so it takes no dependency
   * on the calendar's shape. `time` is the slot's own wall clock; the zone comes from the location
   * Zocdoc described, which is the only zone Zocdoc vouches for.
   */
  const openBooking = (link: ProviderLink, day: string, slot: Slot) => {
    if (!link.npi) return
    const location = slots?.result?.locations.find(
      (candidate) => candidate.locationId === slot.locationId,
    )
    setBooking({
      npi: link.npi,
      day,
      // The raw `HH:mm` the contract's regex accepts — `slotTime` renders "9:00 am", which is a
      // display string and was rejected by input validation for every slot (PD booking fix).
      time: slot.startTime?.slice(11, 16) ?? '',
      ...(location?.timeZone && { timeZone: location.timeZone }),
      ...(slot.locationId && { locationId: slot.locationId }),
      ...(slot.location?.name && { locationName: slot.location.name }),
      ...(slot.location && { locationVirtual: slot.location.virtual }),
      ...(slot.patientType && { patientType: slot.patientType }),
      ...(slot.allowedVisitReasonIds && { allowedVisitReasonIds: slot.allowedVisitReasonIds }),
      ...(slot.excludedVisitReasonIds && { excludedVisitReasonIds: slot.excludedVisitReasonIds }),
    })
  }

  /**
   * Confirms that a Zocdoc provider and a beHuman practitioner are the same person.
   *
   * Deliberately not routed through `run`: a refused link and a half-applied one are both
   * outcomes worth reading, not thrown failures, and the table is re-read either way so the row
   * on screen matches what was actually written.
   */
  const linkPractitioner = async (zocdocProviderId: string, practitionerId: string) => {
    setBusy(true)
    setError(null)
    try {
      const result = await client.zocdocLinkPractitioner({ zocdocProviderId, practitionerId })
      if (result.status === 'conflict' && result.conflict) {
        setError(conflictMessage(result.conflict))
      } else if (result.status === 'refused' && result.refusal) {
        // Zocdoc could not confirm the link, so nothing was written. Said out loud because the
        // alternative is a link that looks fine and silently never publishes.
        setError(result.refusal.detail)
      }
      await load()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not link the practitioner')
    } finally {
      setBusy(false)
    }
  }

  /**
   * Opens the manual link popup for one Zocdoc provider.
   *
   * `zocdocProviderId` is passed in rather than derived here because a group keyed on NPI can
   * hold more than one provider — Zocdoc allows it — and the popup must never guess which of them
   * a click meant.
   */
  const openLink = (group: (typeof groups)[number], zocdocProviderId: string) => {
    const links = group.links.filter((link) => link.zocdocProviderId === zocdocProviderId)
    const currentPractitionerId = links.find((link) => link.practitionerId)?.practitionerId
    const currentName = currentPractitionerId
      ? (group.npi ? (candidates.byNpi.get(group.npi) ?? []) : []).find(
          (candidate) => candidate.practitionerId === currentPractitionerId,
        )?.name
      : undefined

    setLinkTarget({
      zocdocProviderId,
      ...(group.names[0] && { zocdocName: group.names[0] }),
      ...(group.npi && { zocdocNpi: group.npi }),
      ...(group.specialties[0] && { zocdocSpecialty: group.specialties[0] }),
      entityCount: links.length,
      ...(currentPractitionerId && { currentPractitionerId }),
      ...(currentName && { currentPractitionerName: currentName }),
    })
  }

  /**
   * The practitioner half of the group header: who this Zocdoc provider is in beHuman, or the
   * one-click way to say so.
   *
   * It lives on the **group**, not on each row, because the practitioner is a property of the
   * person: one doctor at three locations is three Zocdoc entities but one clinician, and
   * confirming them three times is both busywork and a chance for the rows to end up disagreeing.
   * The per-location half — organization, time zone, switches — stays in each row's own cells.
   */
  const renderPractitionerSync = (group: (typeof groups)[number]) => {
    const providerIds = [...new Set(group.links.map((link) => link.zocdocProviderId))]
    const practitionerIds = [
      ...new Set(group.links.map((link) => link.practitionerId).filter((id) => !!id)),
    ]
    const found = group.npi ? (candidates.byNpi.get(group.npi) ?? []) : []

    /**
     * The way in to the manual link, offered in **every** state — including the two the NPI match
     * withholds, no NPI and one NPI carrying several providers, which are the cases it exists for.
     *
     * One button per Zocdoc provider when the group holds more than one, labelled by id: there is
     * no single provider to link there, and picking one is the operator's call.
     */
    const linkButtons = providerIds.map((zocdocProviderId) => (
      <Button
        key={zocdocProviderId}
        {...SUBTLE_BTN}
        disabled={busy}
        onClick={() => openLink(group, zocdocProviderId)}
      >
        {practitionerIds.length > 0 ? 'Change' : 'Link to beHuman profile'}
        {providerIds.length > 1 ? ` · ${zocdocProviderId}` : ''}
      </Button>
    ))

    // Locations that disagree about who the doctor is. Never resolved silently: the link write
    // leaves such a row alone rather than re-pointing it, and only a human can say which is right.
    if (practitionerIds.length > 1) {
      return (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="behuman-v2-mulish inline-flex items-center rounded-md bg-[#FEF3C7] px-2 py-0.5 text-[11px] font-bold tracking-[0.04em] text-[#B45309] uppercase">
            locations disagree
          </span>
          <span className={HINT}>{practitionerIds.join(' · ')}</span>
          {linkButtons}
        </div>
      )
    }

    if (practitionerIds.length === 1) {
      const practitionerId = practitionerIds[0]!
      const linked = found.find((candidate) => candidate.practitionerId === practitionerId)
      return (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="behuman-v2-mulish inline-flex items-center gap-1 rounded-md bg-[#DCFCE7] px-2 py-0.5 text-[11px] font-bold tracking-[0.04em] text-[#15803D] uppercase">
            <IconCheck size={12} />
            linked
          </span>
          <span className="text-[13px] font-bold text-[#1F2733]">
            {linked?.name ?? practitionerId}
          </span>
          {/* A real anchor: middle-click and "copy link" work, and a new tab keeps this table's
              place. `?practitioner=` opens the calendar on this doctor — visible to a care
              coordinator; a practitioner only ever sees their own. */}
          <a
            href={`/schedule?practitioner=${practitionerId}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${linked?.name ?? practitionerId}'s calendar in a new tab`}
            className="behuman-v2-mulish text-[12px] font-bold text-[#E5533D] no-underline hover:underline"
          >
            Calendar
          </a>
          {/* The failure that otherwise surfaces at the first booking, when there is no Slot to
              claim and no calendar to land on. */}
          {linked && !linked.hasSchedule && (
            <span className="behuman-v2-mulish inline-flex items-center rounded-md bg-[#FEF3C7] px-2 py-0.5 text-[11px] font-bold tracking-[0.04em] text-[#B45309] uppercase">
              no schedule
            </span>
          )}
          {linkButtons}
        </div>
      )
    }

    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {/* The NPI match, when it found something: one click, no popup. Everything else falls
            through to the manual link beside it. */}
        {found
          .filter((candidate) => !candidate.zocdocProviderId)
          .map((candidate) =>
            providerIds.length === 1 ? (
              <Button
                key={candidate.practitionerId}
                size="compact-xs"
                variant="light"
                color="#E5533D"
                leftSection={<IconUserCheck size={14} />}
                className="behuman-v2-mulish !font-bold"
                disabled={busy}
                title={`Write Zocdoc provider ${providerIds[0]} onto ${candidate.name ?? candidate.practitionerId}${candidate.organizationId ? ` (organization ${candidate.organizationId})` : ''}`}
                onClick={() => void linkPractitioner(providerIds[0], candidate.practitionerId)}
              >
                {candidate.name ?? candidate.practitionerId}
                {candidate.hasSchedule ? '' : ' · no schedule'}
              </Button>
            ) : null,
          )}
        <span className={HINT}>
          {/* Unread is not unmatched, and the header says which — the same distinction the
              appointments table draws for an unread patient. */}
          {candidates.error
            ? 'beHuman practitioners could not be read'
            : !group.npi
              ? 'no NPI reported'
              : providerIds.length > 1
                ? `${providerIds.length} Zocdoc providers share this NPI`
                : found.length > 0
                  ? found.length === 1
                    ? 'matched on NPI — confirm'
                    : 'matched on NPI — pick one'
                  : 'no NPI match'}
        </span>
        {linkButtons}
      </div>
    )
  }

  /**
   * One row, rendered the same whether it sits in the telehealth list or behind the collapse.
   *
   * The composite `pr_…|lo_…` is deliberately *not* here any more: it is an api key, not
   * something to read down a column, and Open profile still shows it per location.
   */
  const renderRow = (link: ProviderLink) => {
    const label = labels.byEntity.get(link.zocdocEntityId)
    return (
      <Table.Tr key={link.zocdocEntityId} className={TABLE_ROW}>
        <Table.Td>
          {/* No name here — the group header carries it. What tells one of a
                            doctor's rows from another is the location, so that leads. */}
          <div className="flex flex-wrap items-center gap-1.5">
            {label?.locationName && (
              <span className="text-[13px] font-bold text-[#1F2733]">{label.locationName}</span>
            )}
            {label?.virtual !== undefined && (
              <span
                className={cn([
                  'behuman-v2-mulish inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-bold tracking-[0.04em] uppercase',
                  label.virtual ? 'bg-[#DBEAFE] text-[#1D4ED8]' : 'bg-[#F1F2F4] text-[#6B7280]',
                ])}
              >
                {label.virtual ? 'telehealth' : 'in person'}
              </span>
            )}
          </div>
          <div className={cn([HINT, 'mt-0.5'])}>
            {[label?.city, label?.state].filter(Boolean).join(', ') || '—'}
          </div>
        </Table.Td>
        <Table.Td>
          {link.practitionerId ? (
            <>
              <div className="text-[13px] font-bold text-[#1F2733]">{link.practitionerId}</div>
              <div className={HINT}>{link.organizationId ?? 'no organization'}</div>
            </>
          ) : (
            <span className="behuman-v2-mulish inline-flex items-center rounded-md bg-[#FEF3C7] px-2 py-0.5 text-[11px] font-bold tracking-[0.04em] text-[#B45309] uppercase">
              unmapped
            </span>
          )}
        </Table.Td>
        <Table.Td>
          {/* Zocdoc's own zone for the location, live. There is no local override to drift from. */}
          <div className="text-[13px]">{link.timeZone ?? '—'}</div>
        </Table.Td>
        <Table.Td>
          {isSuperAdmin && (
            <Group gap={4} wrap="nowrap">
              <Button
                {...SUBTLE_BTN}
                disabled={!link.npi}
                title={link.npi ? undefined : 'Zocdoc gave no NPI for this entity'}
                onClick={() => void openProfile(link)}
              >
                Open profile
              </Button>
              <Button
                {...SUBTLE_BTN}
                disabled={!link.npi}
                title={link.npi ? undefined : 'Zocdoc gave no NPI for this entity'}
                onClick={() => void openSlots(link)}
              >
                Slots
              </Button>
            </Group>
          )}
        </Table.Td>
      </Table.Tr>
    )
  }

  return (
    <AdminPanel
      title="Provider links"
      description={
        data
          ? `${data.summary.total} on Zocdoc’s roster · ${data.summary.mapped} mapped`
          : 'Zocdoc’s provider/location pairs, and which beHuman practitioner each one maps to.'
      }
      actions={
        <Button {...PRIMARY_BTN} onClick={() => void load()} loading={busy}>
          Refresh
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {error && (
          <Alert color="red" radius="md">
            {error}
          </Alert>
        )}

        {labels.error && (
          <Alert color="yellow" radius="md">
            {labels.error}. Provider names could not be read from Zocdoc’s directory — the rows
            below are still real links, shown by id.
          </Alert>
        )}

        {data?.links.length === 0 && (
          <p className={cn([HINT, 'm-0'])}>
            Zocdoc’s roster is empty — it lists no provider/location pairs for this account.
          </p>
        )}

        {data && data.links.length > 0 && (
          <Table.ScrollContainer minWidth={900}>
            <Table highlightOnHover verticalSpacing={8} fz="sm">
              <Table.Thead>
                <Table.Tr className={TABLE_ROW}>
                  <Table.Th className={TABLE_HEAD}>Zocdoc entity</Table.Th>
                  <Table.Th className={TABLE_HEAD}>Practitioner / org</Table.Th>
                  <Table.Th className={TABLE_HEAD}>Time zone</Table.Th>
                  <Table.Th className={TABLE_HEAD} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {groups.map((group) => (
                  <Fragment key={group.key}>
                    {/* The doctor, once. Everything below it is one of their provider/location
                        pairs — which is what a Zocdoc "entity" actually is. */}
                    <Table.Tr className="bg-[#F9FAFB]">
                      <Table.Td colSpan={6} className="py-1.5">
                        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                          <div className="flex flex-wrap items-baseline gap-x-2">
                            <span className="text-[13px] font-bold text-[#1F2733]">
                              {group.names.length > 0 ? group.names.join(' · ') : 'Name unknown'}
                            </span>
                            <span className={HINT}>
                              {group.specialties.length > 0
                                ? `${group.specialties.join(' · ')} · `
                                : ''}
                              {group.npi ? `NPI ${group.npi}` : 'no NPI reported'}
                              {` · ${group.links.length} ${group.links.length === 1 ? 'entity' : 'entities'}`}
                            </span>
                          </div>
                          {renderPractitionerSync(group)}
                        </div>
                      </Table.Td>
                    </Table.Tr>
                    {group.telehealth.map((link) => renderRow(link))}
                  </Fragment>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </div>

      <Modal
        classNames={{ content: MODAL_CONTENT }}
        radius={18}
        opened={profile !== null}
        onClose={() => setProfile(null)}
        // Wide enough for the locations table to keep each location on one row.
        size="xl"
        title={profile ? `Zocdoc profile · NPI ${profile.link.npi}` : ''}
      >
        {profile?.loading && <Text size="sm">Reading Zocdoc…</Text>}

        {profile?.error && <Alert color="red">{profile.error}</Alert>}

        {profile?.result?.ok === false && (
          <Alert color="red" title="Zocdoc read failed">
            <Stack gap={2}>
              <Text size="sm">{profile.result.error}</Text>
              {profile.result.status !== undefined && (
                <Text size="sm">HTTP {profile.result.status}</Text>
              )}
              {profile.result.requestId && (
                <Text size="xs" className="!text-[#6B7280]">
                  Zocdoc request id: {profile.result.requestId}
                </Text>
              )}
              {profile.result.missingEnv?.length ? (
                <Text size="sm">Unset: {profile.result.missingEnv.join(', ')}</Text>
              ) : null}
            </Stack>
          </Alert>
        )}

        {profile?.result?.ok && (
          <Stack gap="sm">
            {profile.result.providers?.length ? (
              profile.result.providers.map((provider) => (
                <Stack key={provider.providerId} gap={4}>
                  <Text fw={600}>
                    {[provider.firstName, provider.lastName].filter(Boolean).join(' ') ||
                      'No name on the Zocdoc record'}
                  </Text>
                  <span className={ID_CHIP}>{provider.providerId}</span>
                  <Text size="xs" className="!text-[#6B7280]">
                    {[
                      provider.practiceName,
                      provider.specialties?.join(', '),
                      provider.visitReasonCount
                        ? `${provider.visitReasonCount} visit reasons`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Text>
                  {provider.specialtyIds?.length ? (
                    <Text size="xs" className="!text-[#6B7280]">
                      Specialty ids:{' '}
                      <span className={ID_CHIP}>{provider.specialtyIds.join(', ')}</span>
                    </Text>
                  ) : null}
                  {provider.defaultVisitReasonId && (
                    <Text size="xs" className="!text-[#6B7280]">
                      Default visit reason:{' '}
                      <span className={ID_CHIP}>{provider.defaultVisitReasonId}</span>
                    </Text>
                  )}
                  {/* Hundreds of ids per provider, so they scroll in place rather than pushing
                      the locations and the publish form off the modal. One chip each rather than
                      a comma-joined line: these get copied one at a time into an availability
                      check, and the default is worth being able to pick out. */}
                  {provider.visitReasonIds?.length ? (
                    <Stack gap={4} mt={4}>
                      <Text size="xs" fw={600}>
                        Visit reasons ({provider.visitReasonIds.length})
                      </Text>
                      <ScrollArea.Autosize mah={150}>
                        <div className="flex flex-wrap gap-1 pr-2">
                          {provider.visitReasonIds.map((id) => (
                            <span
                              key={id}
                              className={cn([
                                ID_CHIP,
                                id === provider.defaultVisitReasonId &&
                                  '!bg-[#DCFCE7] !text-[#15803D]',
                              ])}
                              title={
                                id === provider.defaultVisitReasonId
                                  ? 'Zocdoc’s default visit reason for this provider'
                                  : undefined
                              }
                            >
                              {id}
                            </span>
                          ))}
                        </div>
                      </ScrollArea.Autosize>
                    </Stack>
                  ) : null}

                  {/* The composite ids below plus the visit reason above are exactly what an
                      availability check for a single location needs. The tick box is what the
                      publish form below sends as `locationIds`. */}
                  {provider.locations?.length ? (
                    <Stack gap={4} mt={4}>
                      <Text size="xs" fw={600}>
                        Locations ({provider.locations.length})
                      </Text>
                      {/* A table, not stacked text: the entity id is long enough that a location
                          used to wrap onto the next line and run into the one below it. This is
                          also the only place the id is shown now that the card's table drops
                          it. */}
                      <Table.ScrollContainer minWidth={760}>
                        <Table verticalSpacing={4} fz="xs">
                          <Table.Thead>
                            <Table.Tr className={TABLE_ROW}>
                              <Table.Th className={cn([TABLE_HEAD, 'w-9'])}>
                                {/* Publish-all / publish-none for this provider's rows. */}
                                <Checkbox
                                  size="xs"
                                  color="#E5533D"
                                  aria-label="Include every location when publishing"
                                  disabled={telehealthOf(provider.locations).length === 0}
                                  checked={
                                    telehealthOf(provider.locations).length > 0 &&
                                    telehealthOf(provider.locations).every((location) =>
                                      publishLocationIds.has(location.providerLocationId),
                                    )
                                  }
                                  indeterminate={
                                    telehealthOf(provider.locations).some((location) =>
                                      publishLocationIds.has(location.providerLocationId),
                                    ) &&
                                    !telehealthOf(provider.locations).every((location) =>
                                      publishLocationIds.has(location.providerLocationId),
                                    )
                                  }
                                  onChange={(event) =>
                                    toggleAllPublishLocations(
                                      telehealthOf(provider.locations),
                                      event.currentTarget.checked,
                                    )
                                  }
                                />
                              </Table.Th>
                              <Table.Th className={TABLE_HEAD}>Type</Table.Th>
                              <Table.Th className={TABLE_HEAD}>Location</Table.Th>
                              <Table.Th className={TABLE_HEAD}>Where</Table.Th>
                              <Table.Th className={TABLE_HEAD}>Time zone</Table.Th>
                              <Table.Th className={TABLE_HEAD}>Next open</Table.Th>
                              <Table.Th className={TABLE_HEAD}>Entity id</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {provider.locations.map((location) => (
                              <Table.Tr key={location.providerLocationId} className={TABLE_ROW}>
                                <Table.Td>
                                  {/* On-site locations carry no control at all rather than a
                                      disabled one: V1 publishes telehealth availability, and a
                                      greyed box invites a click that would never be allowed. */}
                                  {location.virtual ? (
                                    <Checkbox
                                      size="xs"
                                      color="#E5533D"
                                      aria-label={`Publish availability at ${
                                        location.locationName ?? location.providerLocationId
                                      }`}
                                      checked={publishLocationIds.has(location.providerLocationId)}
                                      onChange={() =>
                                        togglePublishLocation(location.providerLocationId)
                                      }
                                    />
                                  ) : (
                                    <span
                                      className="text-[#9AA0A8]"
                                      title="On-site availability is not published from beHuman"
                                    >
                                      —
                                    </span>
                                  )}
                                </Table.Td>
                                <Table.Td>
                                  <span
                                    className={cn([
                                      'behuman-v2-mulish inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-[11px] font-bold tracking-[0.04em] uppercase',
                                      location.virtual
                                        ? 'bg-[#CCFBF1] text-[#0F766E]'
                                        : 'bg-[#F1F2F4] text-[#6B7280]',
                                    ])}
                                  >
                                    {location.virtual ? 'virtual' : 'in person'}
                                  </span>
                                </Table.Td>
                                <Table.Td className="font-bold text-[#1F2733]">
                                  {location.locationName ?? '—'}
                                </Table.Td>
                                <Table.Td className="!text-[#6B7280]">
                                  {[location.city, location.state].filter(Boolean).join(', ') ||
                                    '—'}
                                </Table.Td>
                                <Table.Td className="!text-[#6B7280]">
                                  {location.timeZone ?? '—'}
                                </Table.Td>
                                <Table.Td className="!text-[#6B7280]">
                                  {location.firstAvailabilityDate ?? '—'}
                                </Table.Td>
                                <Table.Td>
                                  <span className={ID_CHIP}>{location.providerLocationId}</span>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </Table.ScrollContainer>
                    </Stack>
                  ) : (
                    <Text size="xs" className="!text-[#6B7280]">
                      Zocdoc reported no locations for this provider.
                    </Text>
                  )}
                </Stack>
              ))
            ) : (
              <Text size="sm" className="!text-[#6B7280]">
                Zocdoc has no provider for NPI {profile.result.notFound?.join(', ')}.
              </Text>
            )}
            {(profile.result.providers?.length ?? 0) > 1 && (
              <Text size="xs" className="!text-[#6B7280]">
                This NPI carries {profile.result.providers?.length} providers in Zocdoc&apos;s
                directory.
              </Text>
            )}
          </Stack>
        )}

        {profile?.result?.ok && (profile.result.providers?.length ?? 0) > 0 && (
          <Stack gap="sm" mt="lg">
            <Divider label="Publish availability" labelPosition="left" />

            <Group align="flex-end" gap="sm">
              <USDateInput
                classNames={FIELD_CLASSES}
                label="From"
                value={hours.fromDay}
                onChange={(value) => setHours((h) => ({ ...h, fromDay: value ?? '' }))}
                w={140}
              />
              <USDateInput
                classNames={FIELD_CLASSES}
                label="To"
                value={hours.toDay}
                onChange={(value) => setHours((h) => ({ ...h, toDay: value ?? '' }))}
                w={140}
              />
              <TextInput
                classNames={FIELD_CLASSES}
                label="Opens"
                value={hours.startTime}
                onChange={(event) =>
                  setHours((h) => ({ ...h, startTime: event.currentTarget.value }))
                }
                placeholder="09:00"
                w={90}
              />
              <TextInput
                classNames={FIELD_CLASSES}
                label="Closes"
                value={hours.endTime}
                onChange={(event) =>
                  setHours((h) => ({ ...h, endTime: event.currentTarget.value }))
                }
                placeholder="17:00"
                w={90}
              />
              <NumberInput
                classNames={FIELD_CLASSES}
                label="Slot (min)"
                value={hours.slotMinutes}
                onChange={(value) =>
                  setHours((h) => ({ ...h, slotMinutes: typeof value === 'number' ? value : 30 }))
                }
                min={5}
                max={240}
                w={100}
              />
            </Group>

            <Group gap="xs" align="center">
              <Text size="sm">Days</Text>
              <Chip.Group
                multiple
                value={hours.weekdays}
                onChange={(value) => setHours((h) => ({ ...h, weekdays: value }))}
              >
                <Group gap={4}>
                  {WEEKDAYS.map((day) => (
                    <Chip key={day.value} value={day.value} size="xs" variant="outline">
                      {day.label}
                    </Chip>
                  ))}
                </Group>
              </Chip.Group>
            </Group>

            <Text size="xs" className="!text-[#6B7280]">
              Slot length only sets the spacing between starts — Zocdoc derives the appointment
              duration from the visit reason. The time zone comes from Zocdoc&apos;s own record for
              this provider. Slots are created for the{' '}
              <strong>{publishLocationIds.size} telehealth location(s)</strong> ticked above.
              On-site locations are never published from here.
            </Text>

            {publishLocationIds.size === 0 && (
              <Text size="xs" c="red">
                Tick at least one telehealth location — there is nowhere to publish slots to.
              </Text>
            )}

            <Group>
              <Button
                {...SECONDARY_BTN}
                loading={publishing}
                disabled={hours.weekdays.length === 0 || publishLocationIds.size === 0}
                onClick={() => void submitAvailability(true)}
              >
                Preview slots
              </Button>
              {confirmPublish ? (
                <>
                  <Button
                    color="red"
                    loading={publishing}
                    onClick={() => void submitAvailability(false)}
                  >
                    Yes — replace those days
                  </Button>
                  <Button variant="subtle" onClick={() => setConfirmPublish(false)}>
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  color="red"
                  disabled={
                    publishing || hours.weekdays.length === 0 || publishLocationIds.size === 0
                  }
                  onClick={() => setConfirmPublish(true)}
                >
                  Publish to Zocdoc
                </Button>
              )}
            </Group>

            {confirmPublish && (
              <Alert color="red" title="This replaces each day’s availability">
                Zocdoc stores one slot list per provider per date, so publishing{' '}
                <strong>overwrites everything already on those dates</strong> — including slots set
                in Zocdoc directly. The dates are not written atomically: a failure partway leaves
                earlier days replaced.
                {publishLocationIds.size < profileLocationCount && (
                  <>
                    {' '}
                    That replacement covers <strong>every</strong> location, not just the ticked
                    ones — including the {profileLocationCount - publishLocationIds.size} on-site
                    location(s) this screen cannot publish to. They end up with{' '}
                    <strong>no slots</strong> on those dates, so publish only for a provider whose
                    in-person calendar is not kept in Zocdoc.
                  </>
                )}
              </Alert>
            )}

            {availability?.error && (
              <Alert color="red" title="Could not build slots">
                <Stack gap={2}>
                  <Text size="sm">{availability.error}</Text>
                  {availability.unknownLocationIds?.length ? (
                    <Text size="sm">
                      Unknown to Zocdoc: {availability.unknownLocationIds.join(', ')}
                    </Text>
                  ) : null}
                  {availability.missingEnv?.length ? (
                    <Text size="sm">Unset: {availability.missingEnv.join(', ')}</Text>
                  ) : null}
                </Stack>
              </Alert>
            )}

            {availability?.overCap?.length ? (
              <Alert color="red" title="Too many slots for a single day">
                {availability.overCap.map((entry) => (
                  <Text key={entry.date} size="sm">
                    {entry.date}: {entry.slotCount} slots exceeds Zocdoc&apos;s limit of 1500, and a
                    day cannot be split across two requests — the second would replace the first.
                    Narrow the range or lengthen the slot.
                  </Text>
                ))}
              </Alert>
            ) : null}

            {availability && !availability.error && availability.dates.length > 0 && (
              <Alert
                color={
                  availability.dryRun
                    ? 'blue'
                    : availability.dates.every((date) => date.ok)
                      ? 'green'
                      : 'yellow'
                }
                title={
                  availability.dryRun
                    ? `Preview — ${availability.totalSlots} slots across ${availability.dates.length} day(s), nothing sent`
                    : `Published ${availability.dates.filter((d) => d.ok).length}/${availability.dates.length} day(s)`
                }
              >
                <Stack gap={2}>
                  <Text size="xs" className="!text-[#6B7280]">
                    {availability.entityIds.length} location(s) · {availability.timeZone}
                  </Text>
                  {availability.unknownLocationIds?.length ? (
                    <Text size="xs" c="red">
                      Zocdoc has no schedulable pair for{' '}
                      {availability.unknownLocationIds.join(', ')} — those locations were skipped.
                    </Text>
                  ) : null}
                  {availability.dates.map((date) => (
                    <Text key={date.date} size="xs" c={date.ok ? undefined : 'red'}>
                      {date.date}: {date.slotCount} slots
                      {date.ok ? '' : ` — ${date.error ?? 'failed'}`}
                      {date.notFoundLocationIds?.length
                        ? ` (unknown locations: ${date.notFoundLocationIds.join(', ')})`
                        : ''}
                    </Text>
                  ))}
                  {!availability.dryRun && availability.dates.some((date) => !date.ok) && (
                    <Text size="xs" className="!text-[#6B7280]">
                      Publishing stops at the first failure, so later dates were never sent.
                    </Text>
                  )}
                  {!availability.dryRun && (
                    <Text size="xs" className="!text-[#6B7280]">
                      Zocdoc&apos;s slot store is eventually consistent — a read straight after this
                      may not show the new slots yet.
                    </Text>
                  )}
                </Stack>
              </Alert>
            )}
          </Stack>
        )}
      </Modal>

      <Modal
        classNames={{
          content: MODAL_CONTENT,
          title: '!text-[17px] !font-extrabold !tracking-[-0.3px]',
        }}
        radius={18}
        opened={slots !== null}
        onClose={() => setSlots(null)}
        size="xl"
        title={slots ? `Zocdoc calendar · NPI ${slots.link.npi}` : ''}
      >
        {slots && (
          <Stack gap="sm">
            <Group align="flex-end" gap="sm">
              <USDateInput
                classNames={FIELD_CLASSES}
                label="From"
                value={slots.fromDay}
                onChange={(value) =>
                  value && void fetchSlots(slots.link, value, slots.toDay, slots.tab)
                }
                w={140}
              />
              <USDateInput
                classNames={FIELD_CLASSES}
                label="To"
                value={slots.toDay}
                onChange={(value) =>
                  value && void fetchSlots(slots.link, slots.fromDay, value, slots.tab)
                }
                w={140}
              />
              <Button
                {...SECONDARY_BTN}
                loading={slots.loading}
                onClick={() => void fetchSlots(slots.link, slots.fromDay, slots.toDay, slots.tab)}
              >
                Refresh
              </Button>
            </Group>

            {slots.loading && <p className={cn([HINT, 'm-0'])}>Reading Zocdoc…</p>}

            {slots.error && (
              <Alert color="red" radius="md">
                {slots.error}
              </Alert>
            )}

            {slots.result?.error && (
              <Alert color="red" radius="md" title="Could not read the calendar">
                <div className="flex flex-col gap-1 text-[13px]">
                  <span>{slots.result.error}</span>
                  {slots.result.missingEnv?.length ? (
                    <span>Unset: {slots.result.missingEnv.join(', ')}</span>
                  ) : null}
                </div>
              </Alert>
            )}

            {slots.result && !slots.result.error && (
              <>
                <p className={cn([HINT, 'm-0 flex flex-wrap items-center gap-x-2'])}>
                  <span>
                    <span className="font-bold text-[#1F2733]">{slots.result.totalSlots}</span>{' '}
                    bookable slot
                    {slots.result.totalSlots === 1 ? '' : 's'} across {slots.result.days.length} day
                    {slots.result.days.length === 1 ? '' : 's'}
                  </span>
                  {/* Said out loud, because a shorter list than the calendar holds is exactly what
                      a failed publication looks like. */}
                  {slots.result.bookedSlots ? (
                    <span>· {slots.result.bookedSlots} already booked and hidden</span>
                  ) : null}
                  {slots.result.availabilityCheck ? (
                    <span>
                      · for{' '}
                      <span className={ID_CHIP}>
                        {slots.result.availabilityCheck.visitReasonId}
                      </span>{' '}
                      ({slots.result.availabilityCheck.patientType} patient)
                    </span>
                  ) : null}
                  {slots.result.providerId ? (
                    <span className={ID_CHIP}>{slots.result.providerId}</span>
                  ) : null}
                </p>

                {/* Without this the list silently goes back to meaning "published", which is the
                    bug it was built to fix. */}
                {slots.result.availabilityError ? (
                  <Alert
                    color="yellow"
                    radius="md"
                    title="These are published slots, not bookable ones"
                  >
                    <span className="text-[13px]">{slots.result.availabilityError}</span>
                  </Alert>
                ) : null}

                <UnderlineTabs
                  idPrefix="zocdoc-slots-tab"
                  panelId="zocdoc-slots-tabpanel"
                  label="Slot locations"
                  value={slots.tab}
                  onChange={(next) =>
                    setSlots((current) => (current ? { ...current, tab: next } : current))
                  }
                  tabs={SLOT_TABS.map(({ value, label }) => ({
                    id: value,
                    label,
                    count: countSlots(slotsForTab(slots.result?.days ?? [], value)),
                  }))}
                />

                <div
                  role="tabpanel"
                  id="zocdoc-slots-tabpanel"
                  aria-labelledby={`zocdoc-slots-tab-${slots.tab}`}
                >
                  <SlotsTabPanel
                    days={slotsForTab(slots.result.days, slots.tab)}
                    locations={slots.result.locations.filter(
                      (location) => (location.virtual ? 'virtual' : 'onSite') === slots.tab,
                    )}
                    unresolvedLocationIds={
                      // Only the on-site tab claims the undecodable slots, so only it explains them.
                      slots.tab === 'onSite' ? slots.result.unresolvedLocationIds : []
                    }
                    label={SLOT_TABS.find((entry) => entry.value === slots.tab)!.label}
                    onBook={(day, slot) => openBooking(slots.link, day, slot)}
                  />
                </div>

                {slots.result.totalSlots === 0 && (
                  <Alert color="blue" radius="md">
                    Nothing on this provider&apos;s Zocdoc calendar for the range. The read
                    succeeded — an empty calendar is the expected state until availability is
                    published.
                  </Alert>
                )}

                <div className="mt-1 flex flex-col gap-1 border-t border-[#ECEEF1] pt-3">
                  {slots.result.staleWarning && (
                    <p className={cn([HINT, 'm-0'])}>{slots.result.staleWarning}</p>
                  )}
                  <p className={cn([HINT, 'm-0'])}>
                    Times are the provider&apos;s local wall clock, as Zocdoc stores them. Click a
                    slot to book it; hover for its location, zone and patient type.
                  </p>
                </div>
              </>
            )}
          </Stack>
        )}
      </Modal>

      <LinkPractitionerModal
        target={linkTarget}
        onClose={() => setLinkTarget(null)}
        onLinked={() => void load()}
      />

      <BookSlotModal slot={booking} onClose={() => setBooking(null)} />
    </AdminPanel>
  )
}
